/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { createContext, useState, type ReactNode, useEffect, use, useMemo } from 'react';
import { usePostAcknowldegeMutation } from '@/features/rewards/api/postRewardsAcknowledge';
import { useMyProfile } from '@/features/myProfile/api/getMyProfileV2';
import { sessionStorage } from '@/utils/sessionStorage';

type ModalTCPromptProvider = {
  setModalOpen: (open: boolean) => void;
  modalOpen: boolean;
  submitAcknowledgment: () => void;
};

const ModalTCPromptContext = createContext<ModalTCPromptProvider>({} as ModalTCPromptProvider);

export const useModalTCPromptContext = () => {
  const context = use(ModalTCPromptContext);
  if (context === undefined) {
    throw new Error('useModalTCPromptContext must be used within a ModalTCPromptProvider');
  }
  return context;
};

type ModalTCProviderProps = {
  children: ReactNode;
};

export const ModalTCPromptProvider = ({ children }: ModalTCProviderProps) => {
  const { data: myProfile, refetch } = useMyProfile();
  const { mutate: acknowledgementMutation } = usePostAcknowldegeMutation({
    onSuccess: async () => {
      setModalOpen(false);
      await refetch();
    },
  });

  const isPrompt = useMemo((): boolean => {
    if (!myProfile || !myProfile.loyaltyAccountAlerts) {
      return false;
    }

    const loyaltyAccountAlertPromptObject = myProfile.loyaltyAccountAlerts.find(
      (alert) => alert.alertType === 'TC',
    );

    return loyaltyAccountAlertPromptObject?.status === 'PROMPT' || false;
  }, [myProfile]);

  const [modalOpen, setModalOpen] = useState(false);

  const handlePostRequest = () => acknowledgementMutation({ alertType: 'TC' });

  const submitAcknowledgment = () => {
    sessionStorage.removeItem('tcPilotPrompt');
    handlePostRequest();
  };

  useEffect(() => {
    if (isPrompt) {
      setModalOpen(true);
    } else {
      sessionStorage.setItem('tcPilotPrompt', 'false');
      setModalOpen(false);
    }
  }, [isPrompt]);

  return (
    <ModalTCPromptContext
      value={{
        setModalOpen,
        modalOpen,
        submitAcknowledgment,
      }}
    >
      {children}
    </ModalTCPromptContext>
  );
};
